// src/redux/bookingSlice.js — Tripper-side booking state
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCookie } from '../utils/cookies';

const API = process.env.REACT_APP_API_URL;
const hdr = (token) => ({
  'Content-Type': 'application/json',
  'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
  Authorization: token ? `Bearer ${token}` : undefined,
});
const gt = (t) => t.getState().auth.user?.token;

export const fetchPartnerSlots = createAsyncThunk('booking/slots', async ({ partnerId, ...params }, t) => {
  const q = new URLSearchParams(params).toString();
  const r = await fetch(`${API}/api/bookings/partner/${partnerId}/slots?${q}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const createBooking = createAsyncThunk('booking/create', async (body, t) => {
  const r = await fetch(`${API}/api/bookings`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify(body) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchMyBookings = createAsyncThunk('booking/my', async (params = {}, t) => {
  const q = new URLSearchParams(params).toString();
  const r = await fetch(`${API}/api/bookings/my?${q}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const fetchBookingDetail = createAsyncThunk('booking/detail', async (id, t) => {
  const r = await fetch(`${API}/api/bookings/${id}`, { credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

export const payBooking = createAsyncThunk('booking/pay', async ({ id, partnerQrCode }, t) => {
  const r = await fetch(`${API}/api/bookings/${id}/pay`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify({ partnerQrCode }) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, id };
});

export const cancelBooking = createAsyncThunk('booking/cancel', async (id, t) => {
  const r = await fetch(`${API}/api/bookings/${id}/cancel`, { method: 'PATCH', credentials: 'include', headers: hdr(gt(t)) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return { ...d, id };
});

export const reviewBooking = createAsyncThunk('booking/review', async ({ id, rating, review }, t) => {
  const r = await fetch(`${API}/api/bookings/${id}/review`, { method: 'POST', credentials: 'include', headers: hdr(gt(t)), body: JSON.stringify({ rating, review }) });
  const d = await r.json(); if (!r.ok) return t.rejectWithValue(d.error); return d;
});

const bookingSlice = createSlice({
  name: 'booking',
  initialState: {
    slots: [], myBookings: [], myPagination: null, detail: null,
    status: 'idle', actionStatus: 'idle', actionMessage: null, error: null,
  },
  reducers: {
    clearBookingAction(s) { s.actionStatus = 'idle'; s.actionMessage = null; s.error = null; },
    clearSlots(s) { s.slots = []; },
  },
  extraReducers: b => { b
    .addCase(fetchPartnerSlots.fulfilled, (s, a) => { s.slots = a.payload; })
    .addCase(createBooking.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; })
    .addCase(createBooking.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(fetchMyBookings.fulfilled, (s, a) => { s.myBookings = a.payload.bookings; s.myPagination = a.payload.pagination; })
    .addCase(fetchBookingDetail.fulfilled, (s, a) => { s.detail = a.payload; })
    .addCase(payBooking.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.myBookings = s.myBookings.map(b => b._id === a.payload.id ? { ...b, paymentStatus: 'paid', paymentMethod: 'wer' } : b); })
    .addCase(payBooking.rejected, (s, a) => { s.actionStatus = 'failed'; s.error = a.payload; })
    .addCase(cancelBooking.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; s.myBookings = s.myBookings.map(b => b._id === a.payload.id ? { ...b, status: 'cancelled' } : b); })
    .addCase(reviewBooking.fulfilled, (s, a) => { s.actionStatus = 'succeeded'; s.actionMessage = a.payload.message; });
  },
});

export const { clearBookingAction, clearSlots } = bookingSlice.actions;
export default bookingSlice.reducer;
